import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Helmet } from 'react-helmet';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const ExponentCalculator = () => {
  const [base, setBase] = useState('');
  const [exponent, setExponent] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);

  const handleBaseChange = (e) => {
    setBase(e.target.value);
    setError('');
  };

  const handleExponentChange = (e) => {
    setExponent(e.target.value);
    setError('');
  };

  const calculateExponent = () => {
    if (!base || !exponent) {
      setError('Please enter both base and exponent values.');
      return;
    }

    const baseValue = parseFloat(base);
    const exponentValue = parseFloat(exponent);

    if (isNaN(baseValue) || isNaN(exponentValue)) {
      setError('Please enter valid numbers for both base and exponent.');
      return;
    }

    const resultValue = Math.pow(baseValue, exponentValue);
    setResult(resultValue);

    // Update chart data
    setChartData({
      labels: ['Base', 'Exponent', 'Result'],
      datasets: [
        {
          label: 'Exponent Calculation',
          data: [baseValue, exponentValue, resultValue],
          backgroundColor: ['rgba(75, 192, 192, 0.5)', 'rgba(54, 162, 235, 0.5)', 'rgba(255, 206, 86, 0.5)'],
          borderColor: ['rgba(75, 192, 192, 1)', 'rgba(54, 162, 235, 1)', 'rgba(255, 206, 86, 1)'],
          borderWidth: 1
        }
      ]
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    calculateExponent();
  };

  return (
    <>
      <Helmet>
        <title>Exponent Calculator | BioLab Tools</title>
        <meta name="description" content="Calculate exponents with ease. Raise numbers to any power using our accurate calculator." />
        <meta name="keywords" content="exponent calculator, power calculator, math calculator" />
      </Helmet>

      <div className="min-h-screen bg-gray-50 py-8">
        <div className="container mx-auto px-4 max-w-7xl">
          <header className="mb-8 text-center">
            <div className="text-4xl font-bold text-green-600 mb-2">^</div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-2">Exponent Calculator</h1>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Calculate any number raised to any power with our exponent calculator.
            </p>
          </header>

          <div className="bg-white p-6 rounded-lg shadow-md max-w-2xl mx-auto">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Base</label>
                <input
                  type="number"
                  value={base}
                  onChange={handleBaseChange}
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="Enter base number"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Exponent</label>
                <input
                  type="number"
                  value={exponent}
                  onChange={handleExponentChange}
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="Enter exponent"
                />
              </div>

              <button
                type="submit"
                className="w-full bg-green-600 text-white py-3 px-4 rounded-md font-semibold hover:bg-green-700 transition duration-200"
              >
                Calculate
              </button>

              {error && (
                <div className="bg-red-100 text-red-700 p-4 rounded-md mt-4">
                  {error}
                </div>
              )}

              {result !== null && (
                <div className="mt-8">
                  <h2 className="text-2xl font-bold text-gray-800 mb-6">Result</h2>
                  <div className="bg-gray-50 p-6 rounded-lg shadow-sm">
                    <p className="text-lg font-medium">{base} ^ {exponent} = {result.toFixed(4)}</p>
                  </div>

                  <div className="mt-8">
                    <h3 className="text-xl font-bold text-gray-800 mb-4">Visualization</h3>
                    <div className="h-96">
                      <Bar
                        data={chartData}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          scales: {
                            y: {
                              beginAtZero: true,
                              title: { display: true, text: 'Value' }
                            },
                            x: { title: { display: true, text: 'Category' } }
                          },
                          plugins: { title: { display: true, text: 'Exponent Calculation' } }
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}
            </form>
          </div>

          {/* FAQ Section */}
          <div className="mt-16 bg-white p-6 rounded-lg shadow-md max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Frequently Asked Questions (FAQ)</h2>
            <div className="space-y-6">
              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-600 mb-4">What is an exponent?</h3>
                <p className="text-gray-600">
                  An exponent indicates how many times a number (the base) is multiplied by itself. For example, 2^3 means 2 × 2 × 2 = 8.
                </p>
              </div>

              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-600 mb-4">Can I use negative numbers?</h3>
                <p className="text-gray-600">
                  Yes, you can use both positive and negative numbers for both the base and exponent. For example, (-2)^3 = -8.
                </p>
              </div>

              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-600 mb-4">How accurate are the calculations?</h3>
                <p className="text-gray-600">
                  The calculations are highly accurate, using JavaScript's built-in Math.pow() function. Results are rounded to four decimal places for readability.
                </p>
              </div>
            </div>
          </div>

          {/* How to Use Section */}
          <div className="mt-16 bg-white p-6 rounded-lg shadow-md max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">How to Use the Exponent Calculator</h2>
            <div className="space-y-8">
              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 1: Enter Base Number</h3>
                <p className="text-gray-600">
                  Input the base number you want to raise to a power.
                </p>
              </div>

              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 2: Enter Exponent</h3>
                <p className="text-gray-600">
                  Input the exponent (power) to which you want to raise the base number.
                </p>
              </div>

              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 3: Calculate</h3>
                <p className="text-gray-600">
                  Click the "Calculate" button to get the result.
                </p>
              </div>

              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 4: View Result and Visualization</h3>
                <p className="text-gray-600">
                  The result will be displayed, along with a bar chart visualizing the calculation.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ExponentCalculator;